
import LazyHydrate from 'vue-lazy-hydration'
import { addTrailingSlash, diagramToAnalyticsItem, diagramToGA4Item, removeHtml } from '@/utils/helpers'
import analytics from '~/mixins.js/analytics'
import promoBanner from '~/components/banners/promoBaner'

function pickDiagramProperties (diagram) {
  return {
    title: diagram.title,
    name: diagram.name,
    abstract: diagram.abstract,
    details: diagram.details,
    price: diagram.price,
    url: diagram.url,
    id: diagram.id,
    breadcrumbs: diagram.breadcrumbs,
    image: diagram.image,
    for_sale: diagram.for_sale,
    tags: diagram.tags,
    value_description: diagram.value_description,
    slides_count: diagram.slides_count
  }
}

function pickImageProperties (images) {
  return images.map(i => ({
    id: i.id,
    title: i.title,
    full_size: i.full_size,
    medium: i.medium,
    small: i.small,
    number: i.number,
    url: i.url
  })
  )
}

export default {
  components: {
    LazyHydrate,
    promoBanner,
    'id-diagram-lazy-tabs': () => import('~/components/diagram/lazy/Tabs.vue'),
    'id-lazy-breadcrumbs': () => import('~/components/lazy/Breadcrumbs.vue'),
    'id-diagram-lazy-gallery': () => import('~/components/diagram/lazy/Gallery.vue'),
    'id-diagram-lazy-slides': () => import('~/components/diagram/lazy/Slides.vue'),
    'id-diagram-lazy-title': () => import('~/components/diagram/lazy/Title.vue'),
    'id-diagram-lazy-add-to-cart': () => import('~/components/diagram/lazy/AddToCart.vue'),
    'id-favourite-icon': () => import('~/components/favourite/Icon.vue'),
    'id-structured-data-product': () => import('~/components/structured-data/Product.vue')
  },
  mixins: [analytics],
  transition: 'page',
  async asyncData ({
    params,
    $serverBackend,
    payload
  }) {
    if (process.static) {
      const diagram = pickDiagramProperties(payload.diagram)
      const images = pickImageProperties(payload.images)
      const imagesCount = payload.images.length
      return {
        diagram,
        images,
        imagesCount
      }
    }

    const { results: [diagram] } = await $serverBackend.get(
      '/diagrams/', { url: removeHtml(params.diagram) }
    )
    const {
      results: images,
      count: imagesCount
    } = await $serverBackend.get(
      `/diagrams/${diagram.id}/images/`, {
        page_size: 5,
        ordering: 'number'
      }
    )

    return {
      diagram: pickDiagramProperties(diagram),
      images: pickImageProperties(images),
      imagesCount
    }
  },
  data: () => ({
    license: {}
  }),
  head () {
    return {
      meta: [
        {
          hid: 'og:title',
          property: 'og:title',
          content: this.metaTitle
        },
        {
          hid: 'og:site_name',
          property: 'og:site_name',
          content: 'InfoDiagram.com'
        },
        {
          hid: 'og:url',
          property: 'og:url',
          content: this.canonicalUrl
        },
        {
          hid: 'og:image',
          property: 'og:image',
          content: this.metaImage
        },
        {
          hid: 'og:description',
          property: 'og:description',
          content: this.metaDescription
        },
        {
          hid: 'description',
          name: 'description',
          content: this.metaDescription
        },
        {
          hid: 'description',
          name: 'description',
          content: 'Stunning business presentations in no time. Powerful business slides and templates for powerpoint.'
        }
      ],
      title: this.metaTitle,
      description: this.diagram ? this.diagram.title : this.metaDescription,
      bodyAttrs: {
        class: 'page-diagram'
      }
    }
  },
  computed: {
    canonicalUrl () {
      return addTrailingSlash(`${this.$config.baseURL}/diagrams/${this.diagram.url}`)
    },
    metaImage () {
      try {
        return this.diagram.image.full_size.png
      } catch (e) {
        return `${this.$config.baseURL}/infodiagram_og.jpeg`
      }
    },
    metaDescription () {
      try {
        return this.diagram.meta_description
      } catch (e) {
        return 'PowerPoint Presentation Templates, Diagrams, and Charts.'
      }
    },
    metaTitle () {
      try {
        return this.diagram.title
      } catch (e) {
        return 'PowerPoint Presentation Templates, Diagrams, and Charts.'
      }
    },
    licensedPrice () {
      if (this.license.multiplier) {
        return this.diagram.price * this.license.multiplier
      } else {
        return this.diagram.price
      }
    }
  },
  mounted () {
    this.$gtag.event(
      'view_item',
      {
        items: [diagramToAnalyticsItem(this.diagram)]
      }
    )
    this.$gtag.event(
      'view_item',
      {
        send_to: [
          this.$config.ga4TrackingId
        ],
        items: [diagramToGA4Item(this.diagram)]
      }
    )
    if (process.client && window._paq) {
      window._paq.push([
        'setEcommerceView',
        this.diagram.id, // (Required) productSKU
        this.diagram.name, // (Optional) productName
        'Diagram',
        this.diagram.price // (Optional) price
      ])
    }
    this.$bing && this.$bing.event(
      {
        ecomm_prodid: [this.diagram.name.slice(0, 50)],
        ecomm_pagetype: 'product'
      }
    )
  },
  methods: {
    setLicense ({ license }) {
      this.license = license
    }

  }
}
